import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import { getClassesAPI } from "../../../services/portal/admin/classService";
import { scheduleDate, scheduleTime } from "../../../utility/Common";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import ClassDetailsBox from "../../../components/portal/ClassDetailsBox";
import YouTube from "react-youtube";
import { CalendarIcon } from "../../../icons/CalendarIcon";
import { TimeIcon } from "../../../icons/TimeIcon";
import BunnyVideo from "../../../components/BunneyVideo";
import { getPayedClassScheduleByIdAPI } from "../../../services/portal/student/studentService";
import StudyPackCard from "../../../components/portal/StudyPackCard";
import Plyr from "plyr";
import "plyr/dist/plyr.css";

const ClassScheduleDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classId = searchParams.get("classId");
  const scheduleId = searchParams.get("scheduleId");
  const [loading, setLoading] = useState(false);
  const [classDetails, setClassDetails] = useState(null);
  const [scheduleDetails, setScheduleDetails] = useState(null);
  const [youtubeVideoId, setYoutubeVideoId] = useState(null);
  const [documents, setDocuments] = useState([]);

  const playerRef = useRef(null);
  const plyrInstance = useRef(null);

  const extractYouTubeVideoId = (url) => {
    if (!url) return null;

    // Handle various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
      /youtube\.com\/live\/([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  };

  useEffect(() => {
    if (!youtubeVideoId || !playerRef.current) return;

    plyrInstance.current = new Plyr(playerRef.current, {
      title: "",
      controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "fullscreen",
        "settings",
      ],
      settings: ["speed"],
      speed: {
        selected: 1,
      },
      youtube: { noCookie: true, rel: 0, showinfo: 0 },
    });

    return () => {
      plyrInstance.current?.destroy();
      plyrInstance.current = null;
    };
  }, [youtubeVideoId]);

  const loadClassDetails = useCallback(async () => {
    if (!classId) return;

    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        const classItem = res.Data.find((c) => c.ClassId === Number(classId));
        if (classItem) {
          setClassDetails(classItem);
        } else {
          showErrorToastMsg("Class not found");
        }
      }
    } catch (error) {
      console.error("Error loading class details:", error);
      showErrorToastMsg("Failed to load class details");
    }
  }, [classId]);

  const loadScheduleDetails = useCallback(async () => {
    if (!classId || !scheduleId) return;

    setLoading(true);
    try {
      const res = await getPayedClassScheduleByIdAPI(Number(scheduleId));
      if (res && res.IsSuccessful && res.Data) {
        const schedule = res.Data;
        if (schedule) {
          setScheduleDetails(schedule);
          const recordLink = schedule.RecordLink;
          if (recordLink) {
            const videoId = extractYouTubeVideoId(recordLink);
            setYoutubeVideoId(videoId);
          }

          if (schedule.ScheduleFiles) {
            setDocuments(schedule.ScheduleFiles);
          }
        } else {
          showErrorToastMsg("Schedule not found");
        }
      }
    } catch (error) {
      console.error("Error loading schedule details:", error);
      showErrorToastMsg("Failed to load schedule details");
    } finally {
      setLoading(false);
    }
  }, [classId, scheduleId]);

  useEffect(() => {
    if (classId) {
      loadClassDetails();
    }
    if (classId && scheduleId) {
      loadScheduleDetails();
    }
  }, [classId, scheduleId, loadClassDetails, loadScheduleDetails]);

  if (!classId || !scheduleId) {
    return (
      <DashboardLayout
        title="Schedule Details"
        subtitle="Class ID and Schedule ID are required"
      >
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">
            Class ID and Schedule ID are required
          </p>
        </div>
      </DashboardLayout>
    );
  }

  if (loading && !scheduleDetails) {
    return (
      <DashboardLayout
        title="Schedule Details"
        subtitle="Loading schedule information"
      >
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      </DashboardLayout>
    );
  }

  if (!scheduleDetails) {
    return (
      <DashboardLayout title="Schedule Details" subtitle="Schedule not found">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Schedule not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const onReady = (event) => {
    playerRef.current = event.target; // save player instance
  };

  const recordLink = scheduleDetails.RecordLink;

  return (
    <DashboardLayout
      title="Schedule Details"
      subtitle={
        classDetails
          ? `Schedule for ${classDetails.Name}`
          : "View schedule details"
      }
    >
      <div>
        {/* Class Info Card */}
        {classDetails && <ClassDetailsBox classDetails={classDetails} />}

        {/* Schedule Details Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">
            Schedule Information
          </h3>
          <div className="flex gap-4">
            <div>
              {scheduleDetails.Image && (
                <img
                  src={
                    scheduleDetails.Image.startsWith("data:")
                      ? scheduleDetails.Image
                      : `data:image/jpeg;base64,${scheduleDetails.Image}`
                  }
                  alt="Schedule"
                  className="w-32 object-cover rounded"
                />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="text-xs text-gray-500 tracking-wide">
                  Description
                </span>
                <p className="font-semibold text-lg text-gray-900 mt-1">
                  {scheduleDetails.Description}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500 tracking-wide">
                  Start Date & Time
                </span>
                <div className="flex items-center gap-2 text-gray-700">
                  <CalendarIcon />
                  <span className="font-semibold text-lg text-gray-900 mt-1">
                    {scheduleDate(
                      scheduleDetails.StartDate,
                      scheduleDetails.EndDate,
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <TimeIcon />
                  <span className="font-semibold text-lg text-gray-900 mt-1">
                    {scheduleTime(
                      scheduleDetails.StartDate,
                      scheduleDetails.EndDate,
                    )}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500 tracking-wide">
                  Zoom Link
                </span>
                <p className="mt-1">
                  {scheduleDetails.ZoomLink ? (
                    <a
                      href={scheduleDetails.ZoomLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800 underline font-medium"
                    >
                      Join Zoom Meeting
                    </a>
                  ) : (
                    <span className="text-gray-400">Not available</span>
                  )}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500 tracking-wide">
                  Zoom Password
                </span>
                <p className="font-semibold text-lg text-gray-900 mt-1">
                  {scheduleDetails.Password || (
                    <span className="text-gray-400">Not available</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {!loading && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">
              Study Packs
            </h3>
            {documents.length > 0 ? (
              <div className="flex gap-4">
                {documents.map((doc) => {
                  return <StudyPackCard document={doc} />;
                })}
              </div>
            ) : (
              <span className="text-sm text-gray-700">
                No available any study packs
              </span>
            )}
          </div>
        )}

        {scheduleDetails.RecordLink && scheduleDetails.RecordLink.length>0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Video</h3>
            {youtubeVideoId ? (
              <>
                <style>{`
                    .plyr__title { display: none !important; }
                    .plyr__video-embed::before {
                      content: '';
                      position: absolute;
                      top: 0;
                      left: 0;
                      width: 100%;
                      height: 15%;
                      z-index: 10;
                      pointer-events: all;
                      background: transparent;
                    }
                  `}</style>
                <div
                  ref={playerRef}
                  data-plyr-provider="youtube"
                  data-plyr-embed-id={youtubeVideoId}
                />
              </>
            ) : (
              <BunnyVideo videoUrl={recordLink} />
            )}
          </div>
        )}

        {/* <div style={{ position: "relative", width: "100%" }}>
          {youtubeVideoId ? (
            <YouTube
              videoId={youtubeVideoId}
              opts={{
                width: "100%",
                height: "600",
                playerVars: { controls: 1, modestbranding: 1, rel: 0 },
              }}
              onReady={onReady}
            />
          ) : (
            recordLink && <BunnyVideo videoUrl={recordLink} />
          )}
        </div> */}
      </div>
    </DashboardLayout>
  );
};

export default ClassScheduleDetailsPage;
